
import { defineComponent, reactive, onBeforeMount } from 'vue'

// @components
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'

// @module components
import CrmWhatsappFilters from '@/modules/CrmModule/components/CrmWhatsappFilters/index.vue'

// @types
import { GetWhatsappChatsParams, WhatsappChat } from '@/core/types/notifications.types'

// @realisations
import { notifications_realisation } from '@/core/realisations/notifications.realisation'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @helpers
import cut from '@/common/helpers/cut'
import { useFile } from '@/common/composable/useFile'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'

const TABLE_COLUMNS = [
    {
        key: 'id',
        name: '№',
    },
    {
        key: 'invoiceNumber',
        name: 'Номер накладной',
    },
    {
        key: 'phone',
        name: 'Номер телефона',
    },
    {
        key: 'message',
        name: 'Последнее сообщение',
        withHint: true,
    },
    {
        key: 'senderName',
        name: 'Кто последний ответил',
    },
    {
        key: 'sendedAt',
        name: 'Дата и время последнего сообщения',
    },
]

export default defineComponent({
    components: {
        NewTable,
        NewButton,
        Loader,
        NewLimit,
        NewPagination,
        CrmWhatsappFilters,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()
        const tablesStore = useTablesStore()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as WhatsappChat[],
            pagination: {
                page: 1,
                limit: 50,
                total_pages: 1,
                total_items: 20,
            },
            filters: {
                phoneNumber: null as string | null,
                createdAtFrom: null as string | null,
                createdAtTo: null as string | null,
                senderName: null as string | null,
                invoiceNumber: null as string | null,
                orderNumber: null as string | null,
                typeId: null as number | null,
            },
        })

        const loadHistory = async () => {
            try {
                state.is_loading = true

                const params: GetWhatsappChatsParams = {
                    page: state.pagination.page,
                    limit: state.pagination.limit,
                    createdAtFrom: state.filters.createdAtFrom,
                    createdAtTo: state.filters.createdAtTo,
                    phoneNumber: state.filters.phoneNumber,
                    senderName: state.filters.senderName,
                    invoiceNumber: state.filters.invoiceNumber,
                    orderNumber: state.filters.orderNumber,
                    typeId: state.filters.typeId,
                }

                const response = await notifications_realisation.getWhatsappChats(params)
                state.list = response.data

                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadHistory)

        const onFiltersChanged = (filters: GetWhatsappChatsParams) => {
            state.filters.createdAtFrom = filters.createdAtFrom as string | null
            state.filters.createdAtTo = filters.createdAtTo as string | null
            state.filters.phoneNumber = filters.phoneNumber as string | null
            state.filters.senderName = filters.senderName as string | null
            state.filters.invoiceNumber = filters.invoiceNumber as string | null
            state.filters.orderNumber = filters.orderNumber as string | null
            state.filters.typeId = filters.typeId as number | null

            loadHistory()
        }

        const onFiltersReset = () => {
            state.filters.createdAtFrom = null
            state.filters.createdAtTo = null
            state.filters.phoneNumber = null
            state.filters.senderName = null
            state.filters.invoiceNumber = null
            state.filters.orderNumber = null
            state.filters.typeId = null

            loadHistory()
        }

        const onLimitChange = (new_limit: number) => {
            state.pagination.limit = new_limit
            loadHistory()
        }

        const onChangePage = (new_page: number) => {
            state.pagination.page = new_page
            loadHistory()
        }

        const exportExcel = async () => {
            try {
                state.is_loading = true

                const response = await notifications_realisation.exportExcel({ ...state.filters })
                saveFile(response, 'Whatsapp')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Ошибка при скачивании`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            const dateSecond = dateStr.split(' ')[1]
            return dateFirst.split('-').reverse().join('.') + ' ' + dateSecond
        }

        return {
            TABLE_COLUMNS,
            state,
            loadHistory,
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            cut,
            exportExcel,
            formatDate,
            tablesStore,
        }
    },
})
